import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosPromise } from "axios";
import axios from "axios";
import {
  type CategoryPreferences,
  MePreferenceCategoriesApi,
  type MePreferenceCategoriesApiV2MePreferencesCategoriesGetRequest,
  Sorting,
} from "mws-ts-v2-sdk";

let mePreferenceCategoriesApi: MePreferenceCategoriesApi;

const getMePreferenceCategoriesApi = () => {
  if (!mePreferenceCategoriesApi)
    mePreferenceCategoriesApi = new MePreferenceCategoriesApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );

  return mePreferenceCategoriesApi;
};

const getCategoryPreferences = (
  params: MePreferenceCategoriesApiV2MePreferencesCategoriesGetRequest,
): AxiosPromise<CategoryPreferences> =>
  getMePreferenceCategoriesApi().v2MePreferencesCategoriesGet(params);

export const followedCategoriesListQueryKeys = createQueryKeys(
  "followedCategories",
  {
    list: (
      params: MaybeRef<MePreferenceCategoriesApiV2MePreferencesCategoriesGetRequest>,
    ) => ({
      queryKey: [params],
      queryFn: async () => {
        const response = await getCategoryPreferences(unref(params));

        return response.data;
      },
    }),
  },
);

export const meCategoryPreferenceDefaultParams = {
  pageSize: 1000,
  followed: true,
  sortBy: Sorting.Ascending,
} as const;

export const useFollowedCategoriesQuery = (params?: {
  autoRefetch?: boolean;
}) => {
  const { isLoggedIn } = useAuth();
  const featureToggles = useFeatureToggles();
  const isEnabled = computed(
    () => isLoggedIn.value && featureToggles.value.customerPreferences,
  );

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore?.getPlatformConfiguration.platformId,
  );

  return useQuery({
    ...followedCategoriesListQueryKeys.list({
      xPlatformKey: platformId.value,
      ...meCategoryPreferenceDefaultParams,
    }),
    enabled: isEnabled,
    ...(params?.autoRefetch
      ? {
          // refetch every minute - 60000ms
          refetchInterval: params?.autoRefetch && 60000,
        }
      : {
          // Never refetch this, unless the user does this themselves
          staleTime: Infinity,
        }),
  });
};
